/**
 * Генератор CSS-токенов темы из высокоуровневого `WorkspaceTheme`.
 *
 * Из акцента + базовой нейтрали + радиуса + шрифтов + теней собираем полный
 * набор `--color-*`/`--radius*`/`--font-*`/`--shadow-*` для светлого и тёмного
 * режима. Семантические токены (success/warning/danger/score) НЕ трогаем — они
 * остаются из index.css (:root и .dark), поэтому здесь не дублируются.
 */
import {darken, lighten, mix, readableForeground} from './color';
import {NEUTRAL_SCALES} from './palettes';
import type {ShadowLevel, WorkspaceTheme} from './types';

type Vars = Record<string, string>;

/** Нейтральные токены (фоны/бордеры/текст) для режима. */
const neutralTokens = (theme: WorkspaceTheme, mode: 'light' | 'dark'): Vars => {
  const s = NEUTRAL_SCALES[theme.baseNeutral] ?? NEUTRAL_SCALES.slate;
  if (mode === 'light') {
    return {
      '--color-background': s.s50,
      '--color-surface': '#ffffff',
      '--color-foreground': s.s900,
      '--color-muted': s.s100,
      '--color-muted-foreground': s.s500,
      '--color-border': s.s200,
      '--color-secondary': s.s500,
      '--color-secondary-soft': s.s100,
    };
  }
  return {
    '--color-background': s.s950,
    '--color-surface': s.s900,
    '--color-foreground': s.s50,
    '--color-muted': s.s800,
    '--color-muted-foreground': s.s400,
    '--color-border': s.s800,
    '--color-secondary': s.s400,
    '--color-secondary-soft': s.s800,
  };
};

/** Акцентные (primary) токены для режима. */
const accentTokens = (theme: WorkspaceTheme, mode: 'light' | 'dark'): Vars => {
  const accent = theme.accent;
  if (mode === 'light') {
    return {
      '--color-primary': accent,
      '--color-primary-hover': darken(accent, 0.12),
      '--color-primary-soft': mix(accent, '#ffffff', 0.88),
      '--color-primary-foreground': readableForeground(accent),
    };
  }
  const darkBg = NEUTRAL_SCALES[theme.baseNeutral]?.s950 ?? '#020617';
  const brighter = lighten(accent, 0.08);
  return {
    '--color-primary': brighter,
    '--color-primary-hover': lighten(accent, 0.18),
    '--color-primary-soft': mix(accent, darkBg, 0.8),
    '--color-primary-foreground': readableForeground(brighter),
  };
};

/** Набор теней по уровню (масштабируем непрозрачность от базовой). */
const shadowTokens = (level: ShadowLevel): Vars => {
  if (level === 'none') {
    return {
      '--shadow-2xs': 'none',
      '--shadow-xs': 'none',
      '--shadow-sm': 'none',
      '--shadow-md': 'none',
      '--shadow-lg': 'none',
      '--shadow-xl': 'none',
      '--shadow-2xl': 'none',
    };
  }
  const factor: Record<Exclude<ShadowLevel, 'none'>, number> = {
    sm: 0.6,
    md: 1,
    lg: 1.5,
    xl: 2,
  };
  const k = factor[level];
  const a = (base: number) => Math.min(0.5, +(base * k).toFixed(3));
  const c = 'hsl(0 0% 0% /';
  return {
    '--shadow-2xs': `0 1px 2px 0px ${c} ${a(0.04)})`,
    '--shadow-xs': `0 1px 2px 0px ${c} ${a(0.05)})`,
    '--shadow-sm': `0 1px 3px 0px ${c} ${a(0.1)}), 0 1px 2px -1px ${c} ${a(0.1)})`,
    '--shadow-md': `0 4px 6px -1px ${c} ${a(0.1)}), 0 2px 4px -2px ${c} ${a(0.1)})`,
    '--shadow-lg': `0 10px 15px -3px ${c} ${a(0.1)}), 0 4px 6px -4px ${c} ${a(0.1)})`,
    '--shadow-xl': `0 20px 25px -5px ${c} ${a(0.1)}), 0 8px 10px -6px ${c} ${a(0.1)})`,
    '--shadow-2xl': `0 25px 50px -12px ${c} ${a(0.25)})`,
  };
};

/** Радиус/шрифты — не зависят от режима, выносим в :root (наследуются в .dark). */
const sharedTokens = (theme: WorkspaceTheme): Vars => {
  const vars: Vars = {
    '--radius': `${theme.radius}rem`,
    '--radius-card': `${theme.radius}rem`,
    '--radius-sm': `${Math.max(0, theme.radius - 0.2)}rem`,
    ...shadowTokens(theme.shadow),
  };
  if (theme.fontBody) vars['--font-body'] = theme.fontBody;
  if (theme.fontHeading) vars['--font-heading'] = theme.fontHeading;
  if (theme.fontMono) vars['--font-mono'] = theme.fontMono;
  return vars;
};

/** Наборы токенов для светлого и тёмного режима. */
export const buildTokenSets = (
  theme: WorkspaceTheme,
): {root: Vars; light: Vars; dark: Vars} => ({
  root: sharedTokens(theme),
  light: {...neutralTokens(theme, 'light'), ...accentTokens(theme, 'light')},
  dark: {...neutralTokens(theme, 'dark'), ...accentTokens(theme, 'dark')},
});

const block = (selector: string, vars: Vars, extra = ''): string => {
  const body = Object.entries(vars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
  return `${selector} {\n${body}${extra ? `\n${extra}` : ''}\n}`;
};

/**
 * Собрать CSS темы: общий :root (радиус/шрифты/тени + светлые цвета + color-scheme),
 * и `.dark` (тёмные цвета). Инжектится отдельным `<style>` (apply.ts).
 */
export const buildThemeCss = (theme: WorkspaceTheme): string => {
  const {root, light, dark} = buildTokenSets(theme);
  return [
    block(':root', {...root, ...light}, '  color-scheme: light;'),
    block('.dark', dark, '  color-scheme: dark;'),
  ].join('\n');
};
